import { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

/**
 * ModernDatePicker
 * ----------------
 * Props:
 * - placeholder: string
 * - value: string            // ISO date string `YYYY-MM-DD` or empty
 * - onChange: (e: { target: { value: string } }) => void
 */
const ModernDatePicker = ({ placeholder, value, onChange, width='max-w-md', minDate=undefined, maxDate=undefined }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Parse incoming value (ISO `YYYY-MM-DD` or display `DD/MM/YYYY`) into Date
  const parseDateString = (str) => {
    if (!str) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [year, month, day] = str.split('-').map(Number);
      const d = new Date(year, month - 1, day);
      return isNaN(d) ? null : d;
    }
    // DD/MM/YYYY
    const parts = str.split('/').map(Number);
    if (parts.length === 3) {
      const [d, m, y] = parts;
      const dt = new Date(y, m - 1, d);
      return isNaN(dt) ? null : dt;
    }
    return null;
  };

  // Sync internal state when value prop changes
  useEffect(() => {
    const parsed = parseDateString(value);
    if (parsed) {
      setSelectedDate(parsed);
      setCurrentMonth(parsed);
    } else {
      setSelectedDate(null);
      setCurrentMonth(new Date());
    }
  }, [value]);

  // Display formatter: dd/MM/yyyy
  const formatDisplay = (date) =>
    date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  // Output formatter: ISO YYYY-MM-DD
  const formatOutput = (date) => {
    const yyyy = date.getFullYear();
    const mm   = String(date.getMonth() + 1).padStart(2, '0');  // months are 0-indexed
    const dd   = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  

  // Calendar logic
  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getStartDay = (y, m) => new Date(y, m, 1).getDay();
  const handlePrevMonth = () => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1));

  const handleDateClick = (day) => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(d);
    // emit ISO string
    onChange({ target: { value: formatOutput(d) } });
    setIsOpen(false);
  };

  const isSelected = (day) =>
    selectedDate && day === selectedDate.getDate() &&
    currentMonth.getMonth() === selectedDate.getMonth() &&
    currentMonth.getFullYear() === selectedDate.getFullYear();
  const isToday = (day) => {
    const t = new Date();
    return (
      day === t.getDate() &&
      currentMonth.getMonth() === t.getMonth() &&
      currentMonth.getFullYear() === t.getFullYear()
    );
  };

  const renderDays = () => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    const total = getDaysInMonth(y, m);
    const start = getStartDay(y, m);
    // Normalize min/max bounds
    const minBound = minDate ? (minDate instanceof Date ? minDate : parseDateString(minDate)) : null;
    const maxBound = maxDate ? (maxDate instanceof Date ? maxDate : parseDateString(maxDate)) : null;
    const isOutOfRange = (d) => {
      if (!d) return false;
      if (minBound && d < new Date(minBound.getFullYear(), minBound.getMonth(), minBound.getDate())) return true;
      if (maxBound && d > new Date(maxBound.getFullYear(), maxBound.getMonth(), maxBound.getDate())) return true;
      return false;
    };
    const prevM = new Date(y, m - 1, 1);
    const prevTotal = getDaysInMonth(prevM.getFullYear(), prevM.getMonth());
    const grid = [];

    const prevDays = start;
    for (let i = prevTotal - prevDays + 1; i <= prevTotal; i++) grid.push({ day: i, curr: false });
    // current
    for (let i = 1; i <= total; i++) grid.push({ day: i, curr: true });
    // next
    const rem = 7 - (grid.length % 7);
    if (rem < 7) for (let i = 1; i <= rem; i++) grid.push({ day: i, curr: false });

    const weeks = [];
    for (let i = 0; i < grid.length; i += 7) weeks.push(grid.slice(i, i + 7));

    return weeks.map((week, wi) => (
      <div key={wi} className="grid grid-cols-7 gap-0">
        {week.map((item, di) => {
          const sel = item.curr && isSelected(item.day);
          const todayMark = item.curr && isToday(item.day);
          const candidate = item.curr ? new Date(y, m, item.day) : null;
          const disabled = item.curr && isOutOfRange(candidate);
          let cls = 'flex items-center justify-center h-8 w-8 rounded-full ';
          if (disabled) cls += ' cursor-not-allowed text-gray-300';
          else cls += ' cursor-pointer';
          if (!item.curr) cls += ' text-gray-400';
          else if (sel) cls += ' bg-indigo-600 text-white font-medium';
          else if (todayMark) cls += ' border border-indigo-600 text-indigo-600 font-medium';
          else cls += ' hover:bg-gray-200';
          return (
            <div key={di} onClick={() => item.curr && !disabled && handleDateClick(item.day)}>
              <div className={cls} style={{fontSize: "13px"}}>{item.day}</div>
            </div>
          );
        })}
      </div>
    ));
  };

  // month/year dropdowns
  const [showMonth, setShowMonth] = useState(false);
  const [showYear, setShowYear] = useState(false);
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  const years = Array.from({ length: 21 }, (_, i) => new Date().getFullYear() - 10 + i);

  return (
    <div className={`${width}`}>
      <div className="mb-1 text-sm font-medium text-gray-700">{placeholder}</div>
      <div className="relative flex items-center" onClick={() => setIsOpen(o => !o)}>
        <input
          type="text"
          readOnly
          value={selectedDate ? formatDisplay(selectedDate) : ''}
          placeholder="dd/mm/yyyy"
          className="w-full py-2 px-4 border rounded-lg cursor-pointer text-sm"
        />
        <Calendar className="absolute right-3 text-gray-500" size={16} />
      </div>

      {isOpen && (
        <div className="absolute mt-1 bg-white p-3 rounded-lg shadow border w-full z-10" style={{ minWidth: '13vw', maxWidth: "17vw", width: "auto", zIndex: "9999999" }}>
          <div className="flex items-center justify-between mb-4">
            <button onClick={handlePrevMonth}><ChevronLeft style={{height: "15px"}} /></button>
            <div className="relative">
              <div className="flex items-center gap-1 cursor-pointer" onClick={() => { setShowMonth(s => !s); setShowYear(false); }}>
                <span style={{fontSize: "14px"}}>{months[currentMonth.getMonth()]}</span><ChevronDown style={{height: "15px"}} />
              </div>
              {showMonth && (
                <div className="absolute bg-white shadow rounded p-2 grid gap-1 w-40" style={{height: "25vh", overflow: "auto"}}>
                  {months.map((m,i) => (
                    <div key={m} onClick={() => { setCurrentMonth(d => new Date(d.getFullYear(), i, 1)); setShowMonth(false); }}
                      className={`px-2 py-1 cursor-pointer ${i === currentMonth.getMonth() ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
                      style={{fontSize: "13px"}}>{m}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <div className="flex items-center gap-1 cursor-pointer" onClick={() => { setShowYear(s => !s); setShowMonth(false); }}>
                <span style={{fontSize: "14px"}}>{currentMonth.getFullYear()}</span><ChevronDown style={{height: "15px"}}/>
              </div>
              {showYear && (
                <div className="absolute bg-white shadow rounded p-2 max-h-48 overflow-y-auto grid gap-1 w-24">
                  {years.map(y => (
                    <div key={y} onClick={() => { setCurrentMonth(d => new Date(y, d.getMonth(), 1)); setShowYear(false); }}
                      className={`px-2 py-1 cursor-pointer ${y === currentMonth.getFullYear() ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
                      style={{fontSize: "13px"}}>{y}</div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={handleNextMonth}><ChevronRight style={{height: "15px"}}/></button>
          </div>

          <div className="grid grid-cols-7 gap-0 mb-2">
            {['S','M','T','W','T','F','S'].map((d,i) => <div key={i} className="text-center text-xs font-medium text-gray-600">{d}</div>)}
          </div>

          {renderDays()}

          {/* <div className="mt-3 flex justify-between pt-2 border-t">
            <button onClick={() => {
              const today = new Date();
              setSelectedDate(today);
              setCurrentMonth(today);
              onChange({ target: { value: formatOutput(today) } });
            }} className="text-sm text-blue-500">Today</button>
            <button onClick={() => setIsOpen(false)} className="text-sm text-blue-500">Close</button>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default ModernDatePicker;